import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type Challenge, TierName } from '@sg/graphql-schema';

import { type LoyaltyReward } from '../../GraphQL';
import { useLoyaltyInfoQuery } from '../../GraphQL/LoyaltyInfo.generated';

export const useLoyaltyInfo = (props: UseLoyaltyInfoProps) => {
  const { pause } = props;

  const [response, refetch] = useLoyaltyInfoQuery({
    requestPolicy: 'cache-and-network',
    pause,
  });

  const { formatMessage } = useIntl();
  const guest = formatMessage(messages.guest);

  const isLoading = response.fetching;
  const isStale = response.stale;
  const hasFailedToLoad = Boolean(response.error);

  return useMemo(() => {
    const customer = response.data?.currentCustomer;
    const loyaltyProfile = response.data?.loyaltyProfile;
    const customerTier = loyaltyProfile?.tier?.nameV2;
    const isOptedOut = customerTier === TierName.RewardsOptedOut;

    const challenges = response.data?.challenges ?? emptyChallenges;
    const rewards = loyaltyProfile?.rewards ?? emptyRewards;
    const pointsRewards = loyaltyProfile?.pointsRewards ?? emptyRewards;

    return {
      isLoading,
      isStale,
      hasFailedToLoad,
      firstName: customer?.firstName ?? guest,
      availablePoints: loyaltyProfile?.points?.available ?? 0,
      pendingPoints: loyaltyProfile?.points?.pending ?? 0,
      totalPoints: loyaltyProfile?.points?.total ?? 0,
      challenges,
      rewards,
      pointsRewards,
      customerTier,
      isOptedOut,
      refetch,
    };
  }, [
    guest,
    isLoading,
    isStale,
    hasFailedToLoad,
    response.data?.challenges,
    response.data?.currentCustomer,
    response.data?.loyaltyProfile,
    refetch,
  ]);
};

// ─── Constants ──────────────────────────────────────────────────────────────

const emptyChallenges: Challenge[] = [];
const emptyRewards: LoyaltyReward[] = [];

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  guest: {
    defaultMessage: 'Guest',
    description: 'Loyalty | Customer Name | Fallback',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type UseLoyaltyInfoProps = {
  pause: boolean;
};
